import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLogContext } from "../../../contexts/LogContext";
import  useGet from "../../../hooks/LogPost";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { ChevronLeft, ChevronRight } from "lucide-react";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const LogsTable = () => {
  const { filters, formattedFilters } = useLogContext();
  const { makeRequest } = useGet();
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);

  const start = pageIndex * pageSize + 1;

  const queryParams = new URLSearchParams({
    start: start.toString(),
    length: pageSize.toString(),
    ...(filters.operator && { operator: filters.operator }),
    ...(filters.startDate && {
      startDate: formattedFilters.formattedStartDate,
    }),
    ...(filters.endDate && { endDate: formattedFilters.formattedEndDate }),
    ...(filters.ussdCode && { ussdCode: filters.ussdCode }),
  }).toString();

  const {
    data: queryData,
    isLoading: queryLoading,
    error: queryError,
  } = useQuery({
    queryKey: [
      "bvnLogs",
      filters.operator,
      formattedFilters.formattedStartDate,
      formattedFilters.formattedEndDate,
      filters.ussdCode,
      pageSize,
      pageIndex,
    ],
    queryFn: () =>
      makeRequest(`/externalPortal/bvnPortal/nibss/bvnLogs?${queryParams}`),
    enabled: true,
    staleTime: 0,
    gcTime: 15 * 60 * 1000,
  });

  const totalRecords = queryData?.data?.filteredRecordCount
    ? Number(queryData.data.filteredRecordCount)
    : 0;
  const pageCount = Math.ceil(totalRecords / pageSize);

  const formatTimestamp = (tstamp) => {
    try {
      const [datePart, timePart, period] = tstamp.split(" ");
      const [day, month, year] = datePart.split("-");
      const [hours, minutes, secondsAndMicro] = timePart.split(".");
      const seconds = secondsAndMicro.split(".")[0];

      const monthMap = {
        JAN: "01",
        FEB: "02",
        MAR: "03",
        APR: "04",
        MAY: "05",
        JUN: "06",
        JUL: "07",
        AUG: "08",
        SEP: "09",
        OCT: "10",
        NOV: "11",
        DEC: "12",
      };

      const fullYear = `20${year}`;
      let adjustedHours = parseInt(hours, 10);
      if (period === "PM" && adjustedHours < 12) {
        adjustedHours += 12;
      } else if (period === "AM" && adjustedHours === 12) {
        adjustedHours = 0;
      }

      const formattedDate = `${fullYear}-${monthMap[month]}-${day}T${adjustedHours
        .toString()
        .padStart(2, "0")}:${minutes}:${seconds}`;

      const date = new Date(formattedDate);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return tstamp;
    }
  };

  const columns = useMemo(
    () => [
      { accessorKey: "msisdn", header: "MSISDN" },
      { accessorKey: "operator", header: "Operator" },
      { accessorKey: "serviceCode", header: "Service Code" },
      { accessorKey: "timestamp", header: "Timestamp" },
      {
        accessorKey: "tstamp",
        header: "Detailed Timestamp",
        cell: ({ row }) => formatTimestamp(row.original.tstamp),
      },
    ],
    []
  );

  const tableData = useMemo(() => queryData?.data?.logs || [], [queryData]);

  const table = useReactTable({
    data: tableData,
    columns,
    pageCount,
    state: { pagination: { pageIndex, pageSize } },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    onPaginationChange: (updater) => {
      const newState =
        typeof updater === "function"
          ? updater({ pageIndex, pageSize })
          : updater;
      setPageIndex(newState.pageIndex);
      setPageSize(newState.pageSize);
    },
  });

  const formatTableHeader = () => {
    const startDate = filters.startDate
      ? new Date(filters.startDate).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : null;
    const endDate = filters.endDate
      ? new Date(filters.endDate).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : null;

    return endDate && startDate
      ? `Logs from ${startDate} to ${endDate}`
      : startDate
      ? `Logs from ${startDate}`
      : "BVN Logs";
  };

  const exportToXLSX = () => {
    const formattedData = tableData.map((row) => ({
      ...row,
      tstamp: formatTimestamp(row.tstamp),
    }));
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Logs Data");
    XLSX.writeFile(
      workbook,
      `logs_data_${new Date().toISOString().split("T")[0]}.xlsx`
    );
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [columns.map((col) => col.header)],
      body: tableData.map((row) =>
        columns.map((col) =>
          col.accessorKey === "tstamp"
            ? formatTimestamp(row.tstamp)
            : row[col.accessorKey] || ""
        )
      ),
      theme: "striped",
      headStyles: { fillColor: [59, 130, 246] },
    });
    doc.save(`logs_data_${new Date().toISOString().split("T")[0]}.pdf`);
  };

  // Generate page buttons (show up to 5 pages)
  const getPageButtons = () => {
    const maxButtons = 5;
    const buttons = [];
    const half = Math.floor(maxButtons / 2);
    let startPage = Math.max(0, pageIndex - half);
    let endPage = Math.min(pageCount - 1, startPage + maxButtons - 1);

    if (endPage - startPage + 1 < maxButtons) {
      startPage = Math.max(0, endPage - maxButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => setPageIndex(i)}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            pageIndex === i
              ? "bg-blue-600 text-white"
              : "bg-gray-200 text-gray-600 hover:bg-gray-300"
          }`}
        >
          {i + 1}
        </button>
      );
    }

    if (startPage > 0) {
      buttons.unshift(<span key="start-ellipsis">...</span>);
      buttons.unshift(
        <button
          key={0}
          onClick={() => setPageIndex(0)}
          className="px-4 py-2 rounded-md text-sm font-medium bg-gray-200 text-gray-600 hover:bg-gray-300"
        >
          1
        </button>
      );
    }

    if (endPage < pageCount - 1) {
      buttons.push(<span key="end-ellipsis">...</span>);
      buttons.push(
        <button
          key={pageCount - 1}
          onClick={() => setPageIndex(pageCount - 1)}
          className="px-4 py-2 rounded-md text-sm font-medium bg-gray-200 text-gray-600 hover:bg-gray-300"
        >
          {pageCount}
        </button>
      );
    }

    return buttons;
  };

  if (queryLoading) {
    return (
      <div className="p-4 sm:p-6 max-w-full mx-auto">
        <div className="flex justify-center mb-4">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
          <div className="h-8 w-64 bg-gray-200 rounded animate-pulse"></div>
          <div className="space-x-2 mt-4 sm:mt-0">
            <div className="h-10 w-32 bg-gray-200 rounded-md animate-pulse"></div>
            <div className="h-10 w-32 bg-gray-200 rounded-md animate-pulse"></div>
          </div>
        </div>
        <div className="overflow-x-auto bg-white rounded-xl shadow-lg border border-gray-100">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((_, index) => (
                  <th
                    key={index}
                    className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold text-gray-900 uppercase tracking-wider"
                  >
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {[...Array(pageSize)].map((_, rowIndex) => (
                <tr
                  key={rowIndex}
                  className="hover:bg-gray-50 transition-colors"
                >
                  {columns.map((_, colIndex) => (
                    <td
                      key={colIndex}
                      className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-700"
                    >
                      <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-center mt-6 space-y-4 sm:space-y-0 sm:space-x-2">
          <div className="h-10 w-10 bg-gray-200 rounded-full animate-pulse"></div>
          {[...Array(5)].map((_, index) => (
            <div
              key={index}
              className="h-10 w-12 bg-gray-200 rounded-md animate-pulse"
            ></div>
          ))}
          <div className="h-10 w-10 bg-gray-200 rounded-full animate-pulse"></div>
        </div>
      </div>
    );
  }

  if (queryError) {
    return (
      <div className="text-center py-4 text-red-500">
        Error: {queryError.message}
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-full mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">
          {formatTableHeader()}
        </h2>
        <div className="space-x-2 mt-4 sm:mt-0">
          <button
            onClick={exportToXLSX}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            Export to XLSX
          </button>
          <button
            onClick={exportToPDF}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Export to PDF
          </button>
        </div>
      </div>
      <div className="overflow-x-auto bg-white rounded-xl shadow-lg border border-gray-100">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs sm:text-sm font-semibold text-gray-900 uppercase tracking-wider"
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-700"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col sm:flex-row items-center justify-between mt-6 space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-700">Show</span>
          <select
            value={pageSize}
            onChange={(e) => {
              const newSize = Number(e.target.value);
              setPageSize(newSize);
              setPageIndex(0); // Reset to first page when pageSize changes
              table.setPageSize(newSize);
            }}
            className="p-2 border border-gray-300 rounded-md"
          >
            {[10, 15, 20, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="text-sm text-gray-700">entries</span>
        </div>
        <div className="text-sm text-gray-700">
          Showing {pageIndex * pageSize + 1} to{" "}
          {Math.min((pageIndex + 1) * pageSize, totalRecords)} of {totalRecords}{" "}
          entries
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setPageIndex((prev) => Math.max(0, prev - 1))}
            disabled={pageIndex === 0}
            className="p-2 rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          {getPageButtons()}
          <button
            onClick={() => setPageIndex((prev) => Math.min(pageCount - 1, prev + 1))}
            disabled={pageIndex >= pageCount - 1}
            className="p-2 rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogsTable;